/**
 * QRA (quick-research agent) version — a SUPERADMIN-only chat toggle that picks which research agent
 * the coordinator hosts as its `quick_research` AgentTool.
 *
 *  - `2.0` — the production single-agent Smart-RAG QRA, now the PYTHON service
 *            (`services/agent-quick-research/adk_agents/qra_2`). The default for everyone; the only version
 *            non-superadmins ever run. Out of process, and unlike 3.5 it returns the legal result
 *            contract, so moving out of process costs it no structured packet.
 *
 * `2.0-ts` (the same agent, in-process TypeScript) was REMOVED on 2026-08-24. It existed for one
 * reason — `/research` and the MCP/batch runner still called the TypeScript agent directly, so the
 * code had to stay compiled in anyway and a superadmin toggle cost nothing. Both moved to the Python
 * service that day, and an escape hatch that routes a chat turn to a second implementation of the
 * same agent is not a safety net: it is a second thing to keep correct, and a way for two users to
 * get different answers to the same question. `server/agents/quick-research/agent.ts` survives only
 * as the `adk run` / `adk web` standalone entry point, which is a development CLI and not a product
 * surface.
 *  - `2.1` — QRA 2 with the `el-salvador-law` skill in front of its retrieval and NOTHING else: same
 *            model, same two tools, same prompt prefix, same `legal_result` contract
 *            (`adk_agents/qra_2_1_sv`). Experimental, superadmin-only. It exists to answer whether the
 *            skill is the part of 3.5 that helps, which is only answerable while it is the only
 *            difference.
 *  - `3.5` — the standalone Python service (`services/agent-quick-research`): one expert dispatching agentic
 *            query workers, bounded by a hard USD ceiling. It runs OUT of process and reaches the
 *            corpus over `/mcp-agent` itself, so the coordinator calls it over HTTP rather than hosting
 *            an ADK agent. Superadmin-only. Requires `QRA_35_URL`.
 *
 * ─── HOW THE SELECTION ACTUALLY TRAVELS (and how it did not, until #74) ──────────────────────────
 *
 * IT WAS INERT FOR MONTHS. `chat.post.ts` gated the value on superadmin and then dropped the
 * variable, while the Python coordinator picked its research agent from a process-level env var
 * resolved at import — so picking `3.5` in this dropdown changed nothing at all, silently, from the
 * day the coordinator moved out of process. A toggle that looks like it works is worse than one that
 * is absent, which is why this note is here rather than in a commit message.
 *
 * It now rides the SIGNED RUN TICKET, as the `qra` claim, exactly like the model tier beside it
 * (`server/utils/agentUserToken.ts`) — the app decides, the ticket carries, and code applies it. On
 * the far side `marcus_web_chat.coordinator` holds one pre-built remote client per variant and a
 * router agent named `quick_research` picks between them from `Billing.qra`.
 *
 * THE MODEL CANNOT CHOOSE. It sees one tool, with one name and one description, and no variant is
 * named in any prompt — there is no token it could emit that selects an agent. Selection is a dict
 * lookup on a value a superadmin set.
 *
 * v3.4 (the in-process multi-agent `SequentialAgent`) was removed on 2026-07-27: it never populated
 * the shared `LegalEvidenceRegistry`, so its citations and grounding were unwired, and 3.5 supersedes
 * the design it was exploring. The `versions/` tree still holds it for `adk web` experimentation.
 *
 * QRA 3.5 does not populate the shared registry. That does NOT disable grounding: the
 * validator and the citation hovers both work from the answer's `cite:section/<id>` markers,
 * resolved against the DB, so they are version-agnostic and run on every path. (This comment used to
 * claim grounding was skipped; it was stale, and both this session and the ADK session designed
 * around the phantom constraint before catching it.) The registry still backs the STRUCTURED packet
 * — public `/research`, MCP and batch — which is what a non-2.0 version actually gives up.
 *
 * Keep this list in sync with the server switch in `server/agents/web-chat/agent.ts`.
 */
export const QRA_VERSIONS = ['2.1'] as const

export type QraVersion = (typeof QRA_VERSIONS)[number]

export const DEFAULT_QRA_VERSION: QraVersion = '2.1'

export const QRA_VERSION_LABELS: Record<QraVersion, string> = {
  '2.1': 'QRA 2.1 (skill)',
}

/**
 * The served ADK app each version routes to — the folder names under
 * `services/agent-quick-research/adk_agents/`.
 *
 * DECLARED HERE, RESOLVED IN PYTHON. This map is the app's half of a contract the coordinator's
 * router implements (`marcus_web_chat.coordinator.QRA_APPS`), and the two must name the same apps.
 * It lives beside the versions so that adding one without deciding where it routes is visibly
 * incomplete rather than quietly a no-op — the failure this whole file's note is about.
 */
export const QRA_VERSION_APPS: Record<QraVersion, string> = {
  '2.1': 'qra_2_1_sv',
}

export function isQraVersion(value: unknown): value is QraVersion {
  return typeof value === 'string' && (QRA_VERSIONS as readonly string[]).includes(value)
}
